import '@/lib/errorReporter';
import { enableMapSet } from "immer";
enableMapSet();
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import {
  createBrowserRouter,
  RouterProvider,
} from "react-router-dom";
import { QueryClient, QueryClientProvider, onlineManager } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import '@/index.css';
import { FontSizeProvider } from '@/components/FontSizeProvider';
import { AuthBootstrap } from '@/components/auth/AuthBootstrap';
import { installGlobalErrorCapture } from '@/lib/error-store';
import { installNetworkListeners } from '@/lib/network-status';
import { OfflineBanner } from '@/components/OfflineBanner';
import { ErrorNotifier } from '@/components/ErrorNotifier';
import { OfflineError } from '@/lib/api-client';

installGlobalErrorCapture();
installNetworkListeners();

// Sync TanStack Query's online state with browser events
onlineManager.setEventListener((setOnline) => {
  const onlineHandler = () => setOnline(true);
  const offlineHandler = () => setOnline(false);
  window.addEventListener('online', onlineHandler);
  window.addEventListener('offline', offlineHandler);
  return () => {
    window.removeEventListener('online', onlineHandler);
    window.removeEventListener('offline', offlineHandler);
  };
});

import { HomePage } from '@/pages/HomePage'
import { ClientsPage } from '@/pages/ClientsPage'
import { AuditLogsPage } from '@/pages/AuditLogsPage'
import { DailyCollectionsPage } from '@/pages/DailyCollectionsPage'
import { LoanManagementPage } from '@/pages/LoanManagementPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { MessagesPage } from '@/pages/MessagesPage'
import { LoginPage } from '@/pages/LoginPage'
import { ClientDashboard } from '@/pages/client/ClientDashboard'
import { ClientPassbook } from '@/pages/client/ClientPassbook'
import { ClientPayment } from '@/pages/client/ClientPayment'
import { ClientSettings } from '@/pages/client/ClientSettings'
import { registerSW } from 'virtual:pwa-register'

// Only register SW in production — in dev it causes noisy workbox logs
if (import.meta.env.PROD) {
  const updateSW = registerSW({
    onNeedRefresh() {
      if (confirm('New version available! Reload to update?')) {
        updateSW(true)
      }
    },
    onOfflineReady() {
      console.log('App ready to work offline')
    },
  })
} else {
  // Unregister any leftover dev SW
  navigator.serviceWorker?.getRegistrations().then((regs) => {
    for (const r of regs) r.unregister();
  });
}

// Initialize theme based on system preference by default
function initTheme() {
  const saved = localStorage.getItem('theme');
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
  const useDark = !saved || saved === 'system' ? prefersDark : saved === 'dark';
  document.documentElement.classList.toggle('dark', useDark);
  document.documentElement.style.colorScheme = useDark ? 'dark' : 'light';
}
initTheme();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      networkMode: 'offlineFirst',
      retry: (failureCount, error) => {
        if (error instanceof OfflineError) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
      staleTime: 30000,
      refetchOnReconnect: true,
    },
    mutations: {
      networkMode: 'offlineFirst',
      retry: (failureCount, error) => {
        if (error instanceof OfflineError) return false;
        return failureCount < 1;
      },
    },
  },
});

const router = createBrowserRouter([
  {
    path: "/",
    element: <HomePage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/login",
    element: <LoginPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/clients",
    element: <ClientsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/collections",
    element: <DailyCollectionsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/loans",
    element: <LoanManagementPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/reports",
    element: <ReportsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/audit-logs",
    element: <AuditLogsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/messages",
    element: <MessagesPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/messages/:threadId",
    element: <MessagesPage />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/settings",
    element: <SettingsPage />,
    errorElement: <RouteErrorBoundary />,
  },
  // Client Portal Routes
  {
    path: "/client/dashboard",
    element: <ClientDashboard />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/client/passbook",
    element: <ClientPassbook />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/client/payment",
    element: <ClientPayment />,
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: "/client/settings",
    element: <ClientSettings />,
    errorElement: <RouteErrorBoundary />,
  },
], {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <OfflineBanner />
        <ErrorNotifier />
        <AuthBootstrap />
        <FontSizeProvider>
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </FontSizeProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
)
